import { OperationType, Unit } from '@rematter/frontend-lib-api-client';

import { ItemPriceRecord } from '../types/pricing';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';
import { parseZonedDateTime } from '~/shared/utils/dateTime';

export const resolveItemPriceQueryAtom = createQueryAtomFactory(
  'brokerage.resolveItemPrice',
  (params: {
    facilityId?: string;
    itemId: string;
    operationType: OperationType;
    currency: string;
    counterpartyAccountId?: string;
    displayPricingUnit?: Unit;
  }) => ({
    resolveItemPrice: {
      __args: {
        input: {
          facilityId: params.facilityId,
          itemId: params.itemId,
          operationType: params.operationType,
          currency: params.currency,
          counterpartyAccountId: params.counterpartyAccountId,
          displayPricingUnit: params.displayPricingUnit,
        },
      },
      sourceType: true,
      sourceId: true,
      itemPricings: {
        itemPricing: {
          id: true,
          item: {
            id: true,
            name: true,
          },
          pricingUnit: true,
          position: true,
          tiersEffectiveAt: true,
          tiers: {
            id: true,
            maxAmount: true,
            minPrice: {
              formula: true,
              variableIds: true,
            },
            targetPrice: {
              formula: true,
              variableIds: true,
            },
            maxPrice: {
              formula: true,
              variableIds: true,
            },
            scrapValue: true,
            position: true,
            timestamps: {
              createdAt: true,
              updatedAt: true,
            },
          },
          timestamps: {
            createdAt: true,
            updatedAt: true,
          },
        },
        evaluatedTiers: {
          tier: {
            id: true,
            maxAmount: true,
            minPrice: {
              formula: true,
              variableIds: true,
            },
            targetPrice: {
              formula: true,
              variableIds: true,
            },
            maxPrice: {
              formula: true,
              variableIds: true,
            },
            scrapValue: true,
            position: true,
            timestamps: {
              createdAt: true,
              updatedAt: true,
            },
          },
          minPrice: true,
          targetPrice: true,
          maxPrice: true,
        },
      },
    },
  }),
  {
    keepPreviousData: false,
    select: (data): ItemPriceRecord | null => {
      const result = data?.resolveItemPrice;
      if (!result) return null;

      interface RawTier {
        id: string;
        maxAmount: number | string | null;
        minPrice: { formula: string; variableIds: string[] };
        targetPrice: { formula: string; variableIds: string[] };
        maxPrice: { formula: string; variableIds: string[] };
        scrapValue: number | string;
        position: number;
        timestamps: { createdAt: string; updatedAt: string };
      }

      const mapTier = (tier: RawTier) => ({
        id: tier.id,
        maxAmount: tier.maxAmount != null ? Number(tier.maxAmount) : null,
        minPrice: {
          formula: tier.minPrice.formula,
          variableIds: tier.minPrice.variableIds,
        },
        targetPrice: {
          formula: tier.targetPrice.formula,
          variableIds: tier.targetPrice.variableIds,
        },
        maxPrice: {
          formula: tier.maxPrice.formula,
          variableIds: tier.maxPrice.variableIds,
        },
        scrapValue: Number(tier.scrapValue),
        position: tier.position,
        timestamps: {
          createdAt: parseZonedDateTime(tier.timestamps.createdAt),
          updatedAt: parseZonedDateTime(tier.timestamps.updatedAt),
        },
      });

      return {
        sourceType: result.sourceType,
        sourceId: result.sourceId,
        itemPricings: result.itemPricings.map((ep) => ({
          itemPricing: {
            id: ep.itemPricing.id,
            item: {
              id: ep.itemPricing.item.id,
              name: ep.itemPricing.item.name,
            },
            pricingUnit: ep.itemPricing.pricingUnit as Unit,
            position: ep.itemPricing.position,
            tiersEffectiveAt: ep.itemPricing.tiersEffectiveAt
              ? parseZonedDateTime(ep.itemPricing.tiersEffectiveAt)
              : null,
            tiers: ep.itemPricing.tiers.map(mapTier),
            timestamps: {
              createdAt: parseZonedDateTime(
                ep.itemPricing.timestamps.createdAt,
              ),
              updatedAt: parseZonedDateTime(
                ep.itemPricing.timestamps.updatedAt,
              ),
            },
          },
          evaluatedTiers: ep.evaluatedTiers.map((et) => ({
            tier: mapTier(et.tier),
            minPrice: et.minPrice != null ? Number(et.minPrice) : null,
            targetPrice: et.targetPrice != null ? Number(et.targetPrice) : null,
            maxPrice: et.maxPrice != null ? Number(et.maxPrice) : null,
          })),
        })),
      };
    },
  },
);

export const resolveProductPriceQueryAtom = resolveItemPriceQueryAtom;
